/**
 * ThinkingAnimation Component
 *
 * Displays typewriter-style thinking messages with an animated spinner.
 * Used to show AI "thinking" state during demo step processing.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  Sparkles,
  DollarSign,
  Calendar,
  FileText,
  CheckCircle,
  Database,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ThinkingAnimationProps {
  messages: string[];
  currentMessageIndex: number;
  isComplete: boolean;
  stepType?: 'file' | 'dates' | 'text' | 'currency' | 'validation' | 'submit';
  className?: string;
}

const stepIcons: Record<string, React.ReactNode> = {
  file: <FileText className="h-5 w-5" />,
  dates: <Calendar className="h-5 w-5" />,
  text: <Sparkles className="h-5 w-5" />,
  currency: <DollarSign className="h-5 w-5" />,
  validation: <Database className="h-5 w-5" />,
  submit: <CheckCircle className="h-5 w-5" />,
};

export function ThinkingAnimation({
  messages,
  currentMessageIndex,
  isComplete,
  stepType = 'file',
  className,
}: ThinkingAnimationProps) {
  const [displayedText, setDisplayedText] = useState('');
  const currentMessage = messages[currentMessageIndex] || '';

  // Typewriter effect
  useEffect(() => {
    if (isComplete) {
      setDisplayedText(currentMessage);
      return;
    }

    setDisplayedText('');
    let index = 0;
    const interval = setInterval(() => {
      if (index < currentMessage.length) {
        setDisplayedText(currentMessage.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 30);

    return () => clearInterval(interval);
  }, [currentMessage, isComplete]);

  const icon = stepIcons[stepType] || <Loader2 className="h-5 w-5" />;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Message history */}
      <AnimatePresence>
        {messages.slice(0, currentMessageIndex).map((message, index) => (
          <motion.div
            key={`history-${index}`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 0.6, x: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 text-sm text-gray-500"
          >
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>{message}</span>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Current message with typewriter effect */}
      {!isComplete && currentMessage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="text-blue-600"
          >
            {icon}
          </motion.div>
          <div className="flex-1">
            <span className="text-blue-800">{displayedText}</span>
            <motion.span
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className="inline-block w-0.5 h-4 bg-blue-600 ml-0.5 align-middle"
            />
          </div>
        </motion.div>
      )}

      {/* Complete state */}
      {isComplete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-2 text-sm text-green-700"
        >
          <CheckCircle className="h-4 w-4" />
          <span>Step complete</span>
        </motion.div>
      )}
    </div>
  );
}

/**
 * Currency Rate Display Component
 * Shows the exchange rate during currency conversion step
 */
export interface CurrencyRateDisplayProps {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  isVisible: boolean;
}

export function CurrencyRateDisplay({
  fromCurrency,
  toCurrency,
  rate,
  isVisible,
}: CurrencyRateDisplayProps) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 px-5 py-3 text-white shadow-lg"
        >
          <DollarSign className="h-6 w-6" />
          <div className="flex flex-col">
            <span className="text-xs opacity-80">Exchange Rate</span>
            <span className="text-lg font-bold">
              1 {fromCurrency} = {rate.toFixed(2)} {toCurrency}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Validation Summary Component
 * Shows AP invoice matching results
 */
export interface ValidationSummaryProps {
  totalRows: number;
  matchedRows: number;
  missingTransactions: number;
  isVisible: boolean;
}

export function ValidationSummary({
  totalRows,
  matchedRows,
  missingTransactions,
  isVisible,
}: ValidationSummaryProps) {
  const hasMissing = missingTransactions > 0;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className={cn(
            'rounded-xl px-5 py-4 text-white shadow-lg',
            hasMissing
              ? 'bg-gradient-to-r from-amber-500 to-orange-600'
              : 'bg-gradient-to-r from-purple-500 to-indigo-600',
          )}
        >
          <div className="flex items-center gap-3 mb-3">
            <Database className="h-6 w-6" />
            <span className="font-semibold">AP Invoice Validation</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{totalRows}</div>
              <div className="text-xs opacity-80">Total Rows</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-300">
                {matchedRows}
              </div>
              <div className="text-xs opacity-80">Matched</div>
            </div>
            <div>
              <div
                className={cn(
                  'text-2xl font-bold',
                  hasMissing ? 'text-red-200' : 'text-yellow-300',
                )}
              >
                {missingTransactions}
              </div>
              <div className="text-xs opacity-80">Missing</div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
